// Ch78 · THE LIVING PROFILE panel (GR-16 D2 / GR-17).
//
// One row per fact the matcher needs. Each row says the VALUE, WHERE it came
// from, HOW OLD it is, and its STATE:
//   known    fresh — act on it
//   stale    older than its half-life — "still true?" → Confirm (one tap)
//   unknown  nobody told us — "ask" shows the question to text them
// Above the rows: "how well do we understand them" (D2) as a bar, kept
// deliberately SEPARATE from lead quality (D1) — the action comes from the
// pair. Every write is append-only on the server; nothing here edits history.

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  clearClientProfileFact,
  confirmClientProfileFact,
  fetchClientProfile,
  formatProfileValue,
  parseProfileInput,
  setClientProfileFact,
  type ClientProfile,
  type ProfileField,
  type ProfileFieldKey,
} from "../../services/clientProfileService";

export interface LivingProfilePanelProps {
  clientId: string;
  /** Called after any write so the host can refresh derived views. */
  onChanged?: (profile: ClientProfile) => void;
}

const ageLabel = (f: ProfileField): string => {
  if (f.age_days === null || f.age_days === undefined) return "date unknown";
  if (f.age_days === 0) return "today";
  if (f.age_days === 1) return "1 day ago";
  return `${f.age_days} days ago`;
};

export default function LivingProfilePanel({ clientId, onChanged }: LivingProfilePanelProps) {
  const theme = useTheme();
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<ProfileFieldKey | null>(null);
  const [editing, setEditing] = useState<ProfileFieldKey | null>(null);
  const [draft, setDraft] = useState("");
  const [confirmClear, setConfirmClear] = useState<ProfileFieldKey | null>(null);
  const epoch = useRef(0);
  const activeClient = useRef(clientId);
  activeClient.current = clientId;

  const load = useCallback(async () => {
    const requestEpoch = ++epoch.current;
    try {
      const next = await fetchClientProfile(clientId);
      if (requestEpoch !== epoch.current || activeClient.current !== clientId) return;
      if (next.client_id !== clientId) throw new Error("Profile response did not match this client");
      setProfile(next);
      setError(null);
    } catch (err: any) {
      if (requestEpoch === epoch.current && activeClient.current === clientId) setError(err?.message || "Could not load the profile");
    }
  }, [clientId]);

  useEffect(() => {
    setProfile(null);
    setError(null);
    setEditing(null);
    setConfirmClear(null);
    setBusy(null);
    load();
    return () => { epoch.current += 1; };
  }, [load]);

  const apply = async (field: ProfileFieldKey, fn: () => Promise<ClientProfile>) => {
    const requestClient = clientId;
    const requestEpoch = ++epoch.current;
    const isCurrent = () => requestEpoch === epoch.current && activeClient.current === requestClient;
    setBusy(field);
    try {
      const next = await fn();
      if (!isCurrent()) return;
      if (next.client_id !== requestClient) throw new Error("Profile response did not match this client; reload before retrying");
      setProfile(next);
      setError(null);
      onChanged?.(next);
      setEditing(null);
      setConfirmClear(null);
    } catch (err: any) {
      if (isCurrent()) setError(err?.message || "That did not save; reload before retrying");
    } finally {
      if (isCurrent()) setBusy(null);
    }
  };

  const stateColor = (s: ProfileField["state"]) =>
    s === "known" ? theme.palette.success.main : s === "stale" ? theme.palette.warning.main : theme.palette.text.disabled;

  if (error && !profile) {
    return (
      <Alert severity="warning" data-testid="living-profile-error" action={<Button size="small" onClick={load}>Retry</Button>}>
        {error}
      </Alert>
    );
  }
  if (!profile || profile.client_id !== clientId) return <Typography variant="body2" color="text.secondary">Loading the profile…</Typography>;

  const u = profile.understanding;
  const pct = Math.round(u.score * 100);

  return (
    <Stack spacing={1.25} data-testid="living-profile">
      {/* D2 — kept apart from D1 on purpose */}
      <Box>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between">
          <Typography variant="body2" fontWeight={800} data-testid="living-profile-label">
            {u.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" data-testid="living-profile-score">
            {pct}% · {u.known} current · {u.stale} stale · {u.unknown} unknown{u.cleared ? ` · ${u.cleared} cleared` : ""}
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{ height: 6, borderRadius: 3, mt: 0.5, bgcolor: alpha(theme.palette.primary.main, 0.12) }}
        />
        {profile.ask_next && (
          <Typography variant="caption" sx={{ color: "primary.main", display: "block", mt: 0.5 }} data-testid="living-profile-ask">
            Ask next: “{profile.ask_next.question}”
          </Typography>
        )}
        {profile.facts_error && (
          <Typography variant="caption" color="text.disabled" display="block" data-testid="living-profile-ledger-missing">
            History is unavailable — current values cannot be verified.
          </Typography>
        )}
      </Box>
      {error && <Alert severity="warning" onClose={() => setError(null)}>{error}</Alert>}

      <Stack spacing={0.5}>
        {profile.fields.map((f) => {
          const isEditing = editing === f.field;
          return (
            <Box
              key={f.field}
              data-testid={`profile-field-${f.field}`}
              data-state={f.state}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "110px 1fr auto" },
                alignItems: "center",
                gap: 1,
                px: 1,
                py: 0.75,
                borderRadius: 1.5,
                borderLeft: `3px solid ${stateColor(f.state)}`,
                bgcolor: alpha(stateColor(f.state), 0.05),
              }}
            >
              <Typography variant="caption" fontWeight={800} sx={{ textTransform: "uppercase", letterSpacing: ".06em", color: "text.secondary" }}>
                {f.label}
              </Typography>

              {isEditing ? (
                <TextField
                  size="small"
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={f.field === "neighborhoods" || f.field === "must_haves" ? "comma-separated" : f.ask}
                  inputProps={{ "data-testid": `profile-input-${f.field}` }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setEditing(null);
                  }}
                />
              ) : (
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} sx={{ color: f.state === "unknown" ? "text.disabled" : "text.primary" }} data-testid={`profile-value-${f.field}`}>
                    {f.state === "cleared" ? "Cleared — not currently specified" : formatProfileValue(f.field, f.value)}
                  </Typography>
                  {f.state !== "unknown" ? (
                    <Typography variant="caption" color="text.secondary" data-testid={`profile-source-${f.field}`}>
                      {f.source_label} · {ageLabel(f)}
                      {f.state === "stale" ? ` · older than ${f.half_life_days} days — still true?` : ""}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      nobody has told us · {f.ask}
                    </Typography>
                  )}
                </Box>
              )}

              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                {isEditing ? (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={busy !== null || parseProfileInput(f.field, draft) === null}
                      onClick={() => apply(f.field, () => setClientProfileFact(clientId, f.field, parseProfileInput(f.field, draft)))}
                      data-testid={`profile-save-${f.field}`}
                      sx={{ fontWeight: 700 }}
                    >
                      Save
                    </Button>
                    <Button size="small" onClick={() => setEditing(null)}>Cancel</Button>
                  </>
                ) : (
                  <>
                    {f.state === "stale" && (
                      <Tooltip title="Still true today — re-observe it">
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<CheckIcon sx={{ fontSize: 14 }} />}
                          disabled={busy !== null}
                          onClick={() => apply(f.field, () => confirmClientProfileFact(clientId, f.field))}
                          data-testid={`profile-confirm-${f.field}`}
                          sx={{ fontWeight: 700, textTransform: "none" }}
                        >
                          Confirm
                        </Button>
                      </Tooltip>
                    )}
                    {f.state === "unknown" && (
                      <Chip size="small" icon={<HelpOutlineIcon sx={{ fontSize: 14 }} />} label="ask" variant="outlined" sx={{ height: 22 }} />
                    )}
                    {f.state !== "unknown" && f.state !== "cleared" && (
                      <Button size="small" disabled={busy !== null} onClick={() => setConfirmClear(f.field)}
                        data-testid={`profile-clear-${f.field}`}>Clear</Button>
                    )}
                    <IconButton
                      disabled={busy !== null}
                      size="small"
                      aria-label={`Set ${f.label}`}
                      data-testid={`profile-edit-${f.field}`}
                      onClick={() => {
                        setEditing(f.field);
                        setDraft((f.state === "unknown" || f.state === "cleared") ? "" : formatProfileValue(f.field, f.value).replace(/^\$/, ""));
                      }}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </>
                )}
              </Stack>
              {confirmClear === f.field && (
                <Box role="alertdialog" aria-label={`Clear ${f.label}?`} sx={{ gridColumn: "1 / -1" }}>
                  <Typography variant="body2">Clear {f.label}? The old value stays in history and will not be reused. Matching may need a new value before it can continue.</Typography>
                  <Button disabled={busy !== null} onClick={() => apply(f.field, () => clearClientProfileFact(clientId, f.field))}
                    data-testid={`profile-confirm-clear-${f.field}`}>Confirm clear</Button>
                  <Button disabled={busy !== null} onClick={() => setConfirmClear(null)}>Keep value</Button>
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>
      {profile.history_count > 0 && (
        <Typography variant="caption" color="text.disabled">
          {profile.history_count} recorded fact{profile.history_count === 1 ? "" : "s"} · nothing is ever overwritten
        </Typography>
      )}
    </Stack>
  );
}
